import type { ProductNode } from "@/entities/product/api/productService";

import type { FormState } from "./videoCreateTypes";

/**
 * VID-BRIEF-1「选品即带料」纯逻辑(无 DOM,可单测)。
 *
 * 选品(从产品库选)后,产品名 + 勾选卖点随编排 brief 上车,直通 ai 导演 ctx 的
 * productName/sellingPoints 注入位 —— 那个注入位 2026-07 就在,此前纯文案路一直没字段可传。
 *
 * ★卖点**默认全选、可点掉**(老板定案):不是让用户从零挑,是把「不想讲的剔掉」。
 *   全集(All)与勾选集(selected)分开存:草稿往返后产品树不在手上,点掉的想点回来
 *   只能靠 formJson 里存着的全集。
 */

export type ProductBriefFields = Pick<
  FormState,
  | "briefProductId"
  | "briefProductName"
  | "briefSellingPoints"
  | "briefSellingPointsAll"
  | "briefIncludeBrandFacts"
>;

/** 选品 → brief 字段(卖点默认全选;IncludeBrandFacts 不动用户已选的开关值)。 */
export function briefFromNode(
  node: ProductNode,
  keepIncludeBrandFacts?: boolean | null
): ProductBriefFields {
  const points = (node.sellingPoints ?? []).filter((p) => p && p.trim());
  return {
    briefProductId: node.code,
    briefProductName: node.name,
    briefSellingPoints: [...points],
    briefSellingPointsAll: [...points],
    briefIncludeBrandFacts: keepIncludeBrandFacts ?? null,
  };
}

/** 清空(产品被移除)。 */
export function clearedBrief(): ProductBriefFields {
  return {
    briefProductId: null,
    briefProductName: null,
    briefSellingPoints: null,
    briefSellingPointsAll: null,
    briefIncludeBrandFacts: null,
  };
}

/** chips 点选:在勾选集里去/留某条(顺序按全集,勾选集不自排序)。 */
export function toggleSellingPoint(form: ProductBriefFields, point: string): ProductBriefFields {
  const all = form.briefSellingPointsAll ?? [];
  const selected = new Set(form.briefSellingPoints ?? []);
  if (selected.has(point)) {
    selected.delete(point);
  } else if (all.includes(point)) {
    selected.add(point);
  }
  return { ...form, briefSellingPoints: all.filter((p) => selected.has(p)) };
}

/**
 * 实体变化后的对账:**图背书的**产品实体被删 → brief 跟着清,不留「实体没了、卖点还在上车」
 * 的脱钩态(「字段第一次有值会打坏谁」的反向:值背后的实体没了,值也不能赖着)。
 *
 * ★VID-PICK-2:无图选品从来没有实体背书(选的就是「只带卖点」)—— 判据必须看**之前**
 * 有没有该品实体:从没有过 → 实体怎么增删都不动 brief(否则用户传一张无关图/删一张图,
 * 卖点就凭空消失);之前有、现在没了 → 才清。
 */
export function syncBriefWithEntities(
  form: ProductBriefFields & { entities?: { productId?: string | null }[] | null },
  nextEntities: { productId?: string | null }[] | null | undefined
): ProductBriefFields | null {
  if (!form.briefProductId) return null;
  const wasBacked = (form.entities ?? []).some((e) => e.productId === form.briefProductId);
  if (!wasBacked) return null;
  const stillThere = (nextEntities ?? []).some((e) => e.productId === form.briefProductId);
  return stillThere ? null : clearedBrief();
}
